import { Component, inject, OnInit, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { Disclaimer, Event, Fee, MY_DATE_FORMATS, OptionGroup, OptionItem } from '../../../domain/registrator';
import { HtmlEditorComponent } from '../../misc/html-editor/html-editor.component';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { EventService } from '../../../services/event.service';
import { MessageService } from '../../../services/message.service';

export interface FeeForm {
  id: FormControl<number | null>;
  name: FormControl<string>;
  target: FormControl<string>;
  description: FormControl<string>;
  earlyBirdPrice: FormControl<number | null>;
  generalPrice: FormControl<number>;
}

export interface OptionItemForm {
  id: FormControl<number | null>;
  label: FormControl<string>;
  shortLabel: FormControl<string>;
  hint: FormControl<string>;
}

export interface OptionGroupForm {
  id: FormControl<number | null>;
  label: FormControl<string>;
  shortLabel: FormControl<string>;
  hint: FormControl<string>;
  exclusive: FormControl<boolean>;
  minAnswers: FormControl<number>;
  maxAnswers: FormControl<number>;
  optionItems: FormArray<FormGroup<OptionItemForm>>;
}

export interface DisclaimerForm {
  id: FormControl<number | null>;
  title: FormControl<string>;
  body: FormControl<string>;
}

export interface EventForm {
  id: FormControl<number | null>;
  name: FormControl<string>;
  shortName: FormControl<string>;
  invoicePrefix: FormControl<string>;
  pageTitle: FormControl<string>;
  date: FormControl<DateTime | null>;
  endDate: FormControl<DateTime | null>;
  earlyBirdDeadline: FormControl<DateTime | null>;
  registrationOpenDate: FormControl<DateTime | null>;
  registrationCloseDate: FormControl<DateTime | null>;
  url: FormControl<string>;
  location: FormControl<string>;
  shortLocation: FormControl<string>;
  logoFileName: FormControl<string>;
  freeEvent: FormControl<boolean>;
  allowMultipleRegistrations: FormControl<boolean>;
  usesPromoCodes: FormControl<boolean>,
  oneFreeFor: FormControl<number>,
  vatRate: FormControl<number>,
  contactEmail: FormControl<string>,
  hotelBookingUrl: FormControl<string>,
  fees: FormArray<FormGroup<FeeForm>>,
  optionGroups: FormArray<FormGroup<OptionGroupForm>>,
  noHeaderText: FormControl<boolean>,
  headerText: FormGroup<DisclaimerForm>,
  noPaymentConditions: FormControl<boolean>,
  paymentConditions: FormGroup<DisclaimerForm>,
  disclaimers: FormArray<FormGroup<DisclaimerForm>>,
}

@Component({
  selector: 'app-event-edit',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatInputModule,
    MatButtonModule,
    MatTabsModule,
    HtmlEditorComponent
  ],
  providers: [
    provideLuxonDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ],
  templateUrl: './event-edit.component.html',
  styleUrl: './event-edit.component.scss',
})
export class EventEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly eventService = inject(EventService);
  private readonly messageService = inject(MessageService);

  readonly eventForm = new FormGroup<EventForm>({
    id: new FormControl<number | null>(null),

    // Explicit nonNullable flag enforces FormControl<string> / FormControl<number> / FormControl<boolean>
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    shortName: new FormControl('', {
      nonNullable: true, validators: [Validators.required,
      Validators.maxLength(10), Validators.pattern('[a-zA-Z0-9]+')]
    }),
    invoicePrefix: new FormControl('', {
      nonNullable: true, validators: [Validators.required,
      Validators.maxLength(10), Validators.pattern('[a-zA-Z0-9]+')]
    }),
    pageTitle: new FormControl('', { nonNullable: true }),

    date: new FormControl<DateTime | null>(null, { validators: [Validators.required] }),
    endDate: new FormControl<DateTime | null>(null),
    earlyBirdDeadline: new FormControl<DateTime | null>(null),
    registrationOpenDate: new FormControl<DateTime | null>(null, { nonNullable: true, validators: [Validators.required] }),
    registrationCloseDate: new FormControl<DateTime | null>(null, { nonNullable: true, validators: [Validators.required] }),

    url: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    location: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    shortLocation: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    logoFileName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    freeEvent: new FormControl(false, { nonNullable: true }),
    allowMultipleRegistrations: new FormControl(true, { nonNullable: true }),
    usesPromoCodes: new FormControl(false, { nonNullable: true }),
    oneFreeFor: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(10)] }),
    vatRate: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(100)] }),
    contactEmail: new FormControl('registrations@clepa.be', { nonNullable: true, validators: [Validators.required] }),
    hotelBookingUrl: new FormControl('', { nonNullable: true }),

    fees: new FormArray<FormGroup<FeeForm>>([]),
    optionGroups: new FormArray<FormGroup<OptionGroupForm>>([]),

    noHeaderText: new FormControl(false, { nonNullable: true }),
    headerText: new FormGroup<DisclaimerForm>({
      id: new FormControl<number | null>(null),
      title: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
      body: new FormControl('', { nonNullable: true }),
    }),
    noPaymentConditions: new FormControl(false, { nonNullable: true }),
    paymentConditions: new FormGroup<DisclaimerForm>({
      id: new FormControl<number | null>(null),
      title: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
      body: new FormControl('', { nonNullable: true }),
    }),
    disclaimers: new FormArray<FormGroup<DisclaimerForm>>([]),
  });


  event = this.route.parent!.snapshot.data['event']; // added by resolver
  loading = signal<boolean>(false);

  ngOnInit() {
    this.eventForm.controls.noHeaderText.valueChanges
      .subscribe(value => {
        const headerText = this.eventForm.controls.headerText;
        if (value) {
          headerText.controls.title.disable();
          headerText.controls.body.disable();
        } else {
          headerText.controls.title.enable();
          headerText.controls.body.enable();
        }
      });
    this.eventForm.controls.noPaymentConditions.valueChanges
      .subscribe(value => {
        const paymentConditions = this.eventForm.controls.paymentConditions;
        if (value) {
          paymentConditions.controls.title.disable();
          paymentConditions.controls.body.disable();
        } else {
          paymentConditions.controls.title.enable();
          paymentConditions.controls.body.enable();
        }
      });
    this.eventForm.controls.freeEvent.valueChanges
      .subscribe(value => {
        if (value) {
          this.eventForm.controls.usesPromoCodes.disable();
          this.eventForm.controls.oneFreeFor.disable();
          this.eventForm.controls.vatRate.disable();
          this.eventForm.controls.earlyBirdDeadline.disable();
          this.eventForm.controls.invoicePrefix.disable();
          this.eventForm.controls.fees.disable();
        } else {
          this.eventForm.controls.usesPromoCodes.enable();
          this.eventForm.controls.oneFreeFor.enable();
          this.eventForm.controls.vatRate.enable();
          this.eventForm.controls.earlyBirdDeadline.enable();
          this.eventForm.controls.invoicePrefix.enable();
          this.eventForm.controls.fees.enable();
        }
      });
      if (this.event) {
        this.patchEvent(this.event);
        // the resolver data is a snapshot taken when the :eventname route was
        // entered; re-fetch the event so changes made in a previous visit
        // (e.g. fees saved from this component) are reflected when navigating
        // back to the edit page
        const eventShortName = this.route.parent!.snapshot.paramMap.get('eventname');
        if (eventShortName) {
          this.eventService.getEventByName(eventShortName).subscribe(event => {
            if (event) {
              this.event = event;
              this.patchEvent(event);
            }
          });
        }
      }
  }

  private patchEvent(event: any) {
    // the short name identifies the event in urls and invoices;
    // it cannot be changed once the event exists
    this.eventForm.controls.shortName.disable();
    // headerText, paymentConditions and disclaimers are patched explicitly below
    // (they may be null, which FormGroup.patchValue cannot handle)
    const { headerText, paymentConditions, disclaimers, ...scalarValues } = event;
    this.eventForm.patchValue(scalarValues);
    this.eventForm.controls.noHeaderText.setValue(!headerText);
    this.eventForm.controls.noPaymentConditions.setValue(!paymentConditions);
    if (event.freeEvent) {
      // the freeEvent valueChanges subscription only fires on change
      this.eventForm.controls.usesPromoCodes.disable();
      this.eventForm.controls.oneFreeFor.disable();
      this.eventForm.controls.vatRate.disable();
      this.eventForm.controls.earlyBirdDeadline.disable();
      this.eventForm.controls.invoicePrefix.disable();
    }
    this.eventForm.controls.fees.clear();
    for (const fee of event.fees ?? []) {
      this.addFee(fee);
    }
    this.eventForm.controls.optionGroups.clear();
    const groups = [...(event.optionGroups ?? [])].sort((a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0));
    for (const group of groups) {
      this.addOptionGroup(group);
    }
    this.eventForm.controls.headerText.patchValue({
      id: headerText?.id ?? null,
      title: headerText?.title ?? '',
      body: headerText?.body ?? '',
    });
    this.eventForm.controls.paymentConditions.patchValue({
      id: paymentConditions?.id ?? null,
      title: paymentConditions?.title ?? '',
      body: paymentConditions?.body ?? '',
    });
    this.eventForm.controls.disclaimers.clear();
    const sortedDisclaimers = [...(disclaimers ?? [])].sort((a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0));
    for (const disclaimer of sortedDisclaimers) {
      this.addDisclaimer(disclaimer);
    }
  }

  get isFreeEvent(): boolean {
    return this.eventForm.controls.freeEvent.value;
  }

  addFee(fee?: Fee) {
    const feeGroup = new FormGroup<FeeForm>({
      id: new FormControl<number | null>(fee ? fee.id : null),
      name: new FormControl(fee ? fee.name : '', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      target: new FormControl(fee ? fee.target : '', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      description: new FormControl(fee?.description ?? '', { nonNullable: true, validators: [Validators.maxLength(512)] }),
      earlyBirdPrice: new FormControl<number | null>(fee ? fee.earlyBirdPrice : null),
      generalPrice: new FormControl(fee ? fee.generalPrice : 0, { nonNullable: true, validators: [Validators.required, Validators.min(0)] }),
    });
    this.eventForm.controls.fees.push(feeGroup);
    if (this.isFreeEvent) {
      feeGroup.disable();
    }
  }

  removeFee(index: number) {
    this.eventForm.controls.fees.removeAt(index);
  }

  addOptionGroup(group?: OptionGroup) {
    const itemGroups = new FormArray<FormGroup<OptionItemForm>>([]);
    const items = [...(group?.optionItems ?? [])] as OptionItem[];
    items.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    for (const item of items) {
      itemGroups.push(this.buildOptionItem(item));
    }
    const groupFormGroup = new FormGroup<OptionGroupForm>({
      id: new FormControl<number | null>(group ? group.id : null),
      label: new FormControl(group ? group.label : '', { nonNullable: true, validators: [Validators.required, Validators.maxLength(1024)] }),
      shortLabel: new FormControl(group ? group.shortLabel : '', { nonNullable: true, validators: [Validators.required, Validators.maxLength(64)] }),
      hint: new FormControl(group?.hint ?? '', { nonNullable: true, validators: [Validators.maxLength(1024)] }),
      exclusive: new FormControl(group?.exclusive ?? false, { nonNullable: true }),
      minAnswers: new FormControl(group?.minAnswers ?? 0, { nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(50)] }),
      maxAnswers: new FormControl(group?.maxAnswers ?? 0, { nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(50)] }),
      optionItems: itemGroups,
    });
    this.eventForm.controls.optionGroups.push(groupFormGroup);
  }

  private buildOptionItem(item?: OptionItem): FormGroup<OptionItemForm> {
    return new FormGroup<OptionItemForm>({
      id: new FormControl<number | null>(item ? item.id : null),
      label: new FormControl(item ? item.label : '', { nonNullable: true, validators: [Validators.required, Validators.maxLength(1024)] }),
      shortLabel: new FormControl(item ? item.shortLabel : '', { nonNullable: true, validators: [Validators.required, Validators.maxLength(64)] }),
      hint: new FormControl(item?.hint ?? '', { nonNullable: true, validators: [Validators.maxLength(1024)] }),
    });
  }

  addOptionItem(groupIndex: number) {
    (this.eventForm.controls.optionGroups.at(groupIndex).controls.optionItems).push(this.buildOptionItem());
  }

  removeOptionGroup(index: number) {
    this.eventForm.controls.optionGroups.removeAt(index);
  }

  removeOptionItem(groupIndex: number, itemIndex: number) {
    this.eventForm.controls.optionGroups.at(groupIndex).controls.optionItems.removeAt(itemIndex);
  }

  dropOptionGroup(event: CdkDragDrop<any>) {
    const groups = this.eventForm.controls.optionGroups;
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const moved = groups.at(event.previousIndex);
    groups.removeAt(event.previousIndex);
    groups.insert(event.currentIndex, moved);
  }

  dropOptionItem(groupIndex: number, event: CdkDragDrop<any>) {
    const items = this.eventForm.controls.optionGroups.at(groupIndex).controls.optionItems;
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const moved = items.at(event.previousIndex);
    items.removeAt(event.previousIndex);
    items.insert(event.currentIndex, moved);
  }

  addDisclaimer(disclaimer?: Disclaimer) {
    const disclaimerGroup = new FormGroup<DisclaimerForm>({
      id: new FormControl<number | null>(disclaimer?.id ?? null),
      title: new FormControl(disclaimer?.title ?? '', { nonNullable: true, validators: [Validators.maxLength(255)] }),
      body: new FormControl(disclaimer?.body ?? '', { nonNullable: true }),
    });
    this.eventForm.controls.disclaimers.push(disclaimerGroup);
  }

  removeDisclaimer(index: number) {
    this.eventForm.controls.disclaimers.removeAt(index);
  }

  dropDisclaimer(event: CdkDragDrop<any>) {
    const disclaimers = this.eventForm.controls.disclaimers;
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const moved = disclaimers.at(event.previousIndex);
    disclaimers.removeAt(event.previousIndex);
    disclaimers.insert(event.currentIndex, moved);
  }

  save() {
    if (this.eventForm.invalid) {
      this.eventForm.markAllAsTouched();
      this.messageService.showMessage('The form contains errors; please check all tabs', true);
      return;
    }

    const v = this.eventForm.getRawValue();
    const payload = {
      name: v.name,
      shortName: v.shortName,
      invoicePrefix: v.invoicePrefix,
      pageTitle: v.pageTitle,
      date: this.toIsoDate(v.date),
      endDate: this.toIsoDate(v.endDate),
      earlyBirdDeadline: this.toIsoDate(v.earlyBirdDeadline),
      registrationOpenDate: this.toIsoDate(v.registrationOpenDate),
      registrationCloseDate: this.toIsoDate(v.registrationCloseDate),
      url: v.url,
      location: v.location,
      shortLocation: v.shortLocation,
      logoFileName: v.logoFileName,
      freeEvent: v.freeEvent,
      allowMultipleRegistrations: v.allowMultipleRegistrations,
      usesPromoCodes: v.usesPromoCodes,
      oneFreeFor: v.oneFreeFor,
      vatRate: v.vatRate,
      contactEmail: v.contactEmail,
      hotelBookingUrl: v.hotelBookingUrl,
      fees: (v.fees ?? []).map(f => ({
        id: f.id,
        name: f.name,
        target: f.target,
        description: f.description,
        earlyBirdPrice: f.earlyBirdPrice,
        generalPrice: f.generalPrice,
      })),
      optionGroups: (v.optionGroups ?? []).map((g: any) => ({
        id: g.id,
        label: g.label,
        shortLabel: g.shortLabel,
        hint: g.hint || null,
        exclusive: g.exclusive,
        // an exclusive group is a radio group: exactly one answer
        minAnswers: g.exclusive ? 1 : g.minAnswers,
        maxAnswers: g.exclusive ? 1 : g.maxAnswers,
        optionItems: (g.optionItems ?? []).map((it: any) => ({
          id: it.id,
          label: it.label,
          shortLabel: it.shortLabel,
          hint: it.hint || null,
        })),
      })),
      // when 'No header text' / 'No payment conditions' is checked, send null
      // so the backend removes the corresponding disclaimer row (orphan removal)
      headerText: v.noHeaderText ? null : {
        id: v.headerText.id,
        title: v.headerText.title,
        body: v.headerText.body,
      },
      paymentConditions: v.noPaymentConditions ? null : {
        id: v.paymentConditions.id,
        title: v.paymentConditions.title,
        body: v.paymentConditions.body,
      },
      disclaimers: (v.disclaimers ?? []).map((d: any) => ({
        id: d.id,
        title: d.title,
        body: d.body,
      })),
    };

    this.loading.set(true);
    if (this.event) {
      this.eventService.updateEvent(this.event.shortName, payload).subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.showMessage(`Event saved`);
          this.router.navigate(['..'], { relativeTo: this.route });
        },
        error: (error) => {
          this.loading.set(false);
          this.handleError(error, 'saving the event');
        }
      });
    } else {
      this.eventService.createEvent(payload).subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.showMessage(`Event created`);
          this.router.navigate(['/admin/events', payload.shortName.toUpperCase()]);
        },
        error: (error) => {
          this.loading.set(false);
          this.handleError(error, 'creating the event');
        }
      });
    }
  }

  cancel() {
    if (this.event) {
      this.router.navigate(['..'], { relativeTo: this.route });
    } else {
      this.router.navigate(['/admin/events']);
    }
  }

  private toIsoDate(value: DateTime | string | null): string | null {
    if (!value) {
      return null;
    }
    return typeof value === 'string' ? value : value.toISODate();
  }

  private handleError(error: any, action: string) {
    console.error(`Error ${action}`, error);
    const message = error?.error?.message ?? error?.error?.detail;
    this.messageService.showMessage(message ? `Error ${action}: ${message.toLowerCase()}` : `Error ${action}`, true);
  }
}
